import React, { useState } from 'react';
import { FilterBar } from '../components/FilterBar';
import { useSchoolSearch } from '../hooks/useSchoolSearch';
import { useGeoLocation } from '../hooks/useGeoLocation';
import { useMetaTags } from '../hooks/useMetaTags';
import { areaOfStudyData } from '../data/areaOfStudyData';
import { getDefaultProgram } from '../data/filterOptions';
import { SchoolListing } from '../types/api';
import { ExternalLinkIcon, ShieldCheckIcon, StarIcon, BuildingIcon, MapPinIcon } from 'lucide-react';
export function CollegesPage() {
  const [degreeLevel, setDegreeLevel] = useState<number>(3);
  const [selectedAreaId, setSelectedAreaId] = useState<number>(1);
  const [selectedProgram, setSelectedProgram] = useState<number>(100);
  // Meta tags
  useMetaTags({
    title: 'Explore Accredited Online Colleges & Universities | DegreeCompare',
    description: 'Browse top-rated accredited online colleges and universities. Compare programs, read reviews, and find the right school for your online degree.',
    keywords: 'online colleges, accredited universities, online degree programs, best online schools, college directory, online education, distance learning',
    ogTitle: 'Explore Accredited Online Colleges & Universities',
    ogDescription: 'Browse and compare top-rated accredited online colleges. Find the right school for your online degree with detailed program information and reviews.'
  });
  const {
    geo
  } = useGeoLocation();
  const {
    schools,
    loading,
    error
  } = useSchoolSearch({
    degreeInterest: degreeLevel,
    mProgram: selectedProgram,
    zipCode: geo.zipCode
  });
  const handleAreaChange = (areaId: number) => {
    setSelectedAreaId(areaId);
    setSelectedProgram(getDefaultProgram(areaId));
  };
  return <div data-id="element-812">
      {/* Hero */}
      <section className="w-full bg-[#1a2332] text-white py-10 md:py-14 px-4 relative overflow-hidden" data-id="element-813">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#2a374a] to-transparent opacity-50 pointer-events-none" data-id="element-814"></div>
        <div className="max-w-7xl mx-auto relative z-10" data-id="element-815">
          <div className="flex items-center gap-3 mb-4" data-id="element-816">
            <div className="bg-white/10 p-2 rounded-lg" data-id="element-817">
              <BuildingIcon className="w-6 h-6" data-id="element-818" />
            </div>
            <span className="text-sm font-medium text-gray-300 uppercase tracking-wider" data-id="element-819">
              College Directory
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-3" data-id="element-820">
            Explore Accredited Online Colleges
          </h1>
          <p className="text-base text-gray-300 max-w-xl" data-id="element-821">
            Browse top-rated online colleges and universities. Click any school
            to learn more about their programs.
          </p>
        </div>
      </section>

      {/* Filter Bar */}
      <div className="w-full bg-white border-b border-gray-200 py-4 px-4 shadow-sm relative z-30" data-id="element-822">
        <div className="max-w-7xl mx-auto" data-id="element-823">
          <FilterBar degreeLevel={degreeLevel} areaStudy={selectedAreaId} program={selectedProgram} onDegreeLevelChange={setDegreeLevel} onAreaStudyChange={handleAreaChange} onProgramChange={id => setSelectedProgram(id)} data-id="element-824" />
        </div>
      </div>

      {/* College Grid */}
      <div className="max-w-7xl mx-auto px-4 py-8 md:py-10" data-id="element-825">
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6" data-id="element-826">
            <p className="font-medium" data-id="element-827">Error loading colleges</p>
            <p className="text-sm" data-id="element-828">{error}</p>
          </div>}

        {loading ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-829">
            {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse" data-id="element-830">
                <div className="w-16 h-16 bg-gray-200 rounded-lg mb-4" data-id="element-831"></div>
                <div className="h-5 bg-gray-200 rounded w-3/4 mb-3" data-id="element-832"></div>
                <div className="h-4 bg-gray-200 rounded w-full mb-2" data-id="element-833"></div>
                <div className="h-4 bg-gray-200 rounded w-2/3" data-id="element-834"></div>
              </div>)}
          </div> : schools.length === 0 ? <div className="bg-gray-50 border border-gray-200 rounded-xl p-12 text-center" data-id="element-835">
            <BuildingIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" data-id="element-836" />
            <p className="text-gray-600 font-medium" data-id="element-837">
              No colleges found for this selection.
            </p>
            <p className="text-sm text-gray-500 mt-1" data-id="element-838">
              Try adjusting your degree level or program filters.
            </p>
          </div> : <>
            <p className="text-sm text-gray-500 mb-6" data-id="element-839">
              Showing {schools.length} college{schools.length !== 1 ? 's' : ''}{' '}
              for your selection
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-840">
              {schools.map((school, index) => <CollegeCard key={`${school.mBrandID}-${index}`} school={school} data-id="element-841" />)}
            </div>
          </>}
      </div>
    </div>;
}
function CollegeCard({
  school
}: {
  school: SchoolListing;
}) {
  return <a href={school.clickURL} target="_blank" rel="noopener noreferrer" className="group bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg hover:border-gray-300 transition-all flex flex-col" data-id="element-842">
      <div className="p-5 flex flex-col flex-grow" data-id="element-843">
        {/* Logo + Name */}
        <div className="flex items-start gap-4 mb-4" data-id="element-844">
          <div className="w-14 h-14 flex-shrink-0 flex items-center justify-center bg-gray-50 rounded-lg border border-gray-100 p-1.5" data-id="element-845">
            <img src={school.imageURL} alt={`${school.mBrandName} logo`} className="max-w-full max-h-full object-contain" data-id="element-846" />
          </div>
          <div className="min-w-0" data-id="element-847">
            <h3 className="text-base font-bold text-gray-900 group-hover:text-[#3b82f6] transition-colors leading-snug" data-id="element-848">
              {school.mBrandName}
            </h3>
            {school.schoolHighlight && <span className="inline-flex items-center gap-1 mt-1 text-[11px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full" data-id="element-849">
                <StarIcon className="w-3 h-3 fill-current" data-id="element-850" />
                {school.schoolHighlight}
              </span>}
          </div>
        </div>

        {/* Description */}
        {school.aboutCollege ? <p className="text-sm text-gray-600 leading-relaxed mb-4 flex-grow line-clamp-3" data-id="element-851">
            {school.aboutCollege}
          </p> : school.headContent ? <p className="text-sm text-gray-600 leading-relaxed mb-4 flex-grow line-clamp-3" data-id="element-852">
            {school.headContent}
          </p> : <p className="text-sm text-gray-600 leading-relaxed mb-4 flex-grow" data-id="element-853">
            Accredited online programs with flexible scheduling options.
          </p>}

        {/* Accreditation */}
        {school.accreditation && <div className="flex items-start gap-2 mb-4 text-xs text-gray-500" data-id="element-854">
            <ShieldCheckIcon className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-855" />
            <span className="line-clamp-1" data-id="element-856">{school.accreditation}</span>
          </div>}

        {/* CTA */}
        <div className="pt-3 border-t border-gray-100 mt-auto flex items-center justify-between" data-id="element-857">
          <span className="text-xs text-gray-500" data-id="element-858">100% Online</span>
          <span className="text-sm font-bold text-[#e8594f] flex items-center gap-1.5 group-hover:gap-2 transition-all" data-id="element-859">
            Visit School <ExternalLinkIcon className="w-3.5 h-3.5" data-id="element-860" />
          </span>
        </div>
      </div>
    </a>;
}